import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { DocumentActions } from '../actions/document/document.actions';

import type { Document as BsmDocument } from '../interfaces/document/document.interface';
import type { FileData } from '../interfaces/config/file.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import KvRow from './components/KvRow';
import FileIcon from './components/FileIcon';

import DocumentEditModal from './DocumentEditModal';
import { fileExtension, formatBytes, deriveAccess, formatDate } from './lib/mandate-helpers';

// =============================================================================
//  Document detail — v3 redesign
//
//  Read-only metadata + inline preview for a single firm-wide document
//  (firm policies, templates, BSM Goals, etc.).  This is the "missing"
//  detail surface flagged in the v3 UX audit — until now, the only way
//  to interact with a document from the v3 list was the inline edit modal.
//
//  Layout idiom matches MandateDetail / ClientDetail / EmployeeDetail /
//  TombstoneDetail so users learn one pattern across surfaces.
//
//  Preview strategy
//  ----------------
//  PDFs render inline via <iframe>.  Other formats (DOCX, XLSX, PPTX) get
//  a download link — browsers can't display Office formats inline without
//  a renderer.  We deliberately don't proxy Office files through a
//  third-party viewer (Google Docs Viewer etc.) because the backend's S3
//  URLs are signed and short-lived; we'd lose the access scope intent.
//
//  Source jbuilder fields:
//    id, name, document_type, category, category_id, sub_category,
//    sub_category_id, user (uploader full_name), access_scope (v3),
//    created_at, data[] (Active Storage attachments — see FileData)
// =============================================================================

const DOC_TYPE_LABEL: Record<string, string> = {
  contract: 'Contract',
  info: 'Information',
  presentation: 'Presentation'
};

const looksLikePdf = (filename?: string): boolean =>
  !!filename && filename.toLowerCase().endsWith('.pdf');

const DocumentDetailV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const documents: BsmDocument[] =
    useAppSelector(({ document_store }) => document_store.documents) || [];

  const [editing, setEditing] = useState(false);
  // Which attached file is currently previewed.  Most documents have one
  // file, but the schema allows multiple — we let the user switch between
  // them via the file list in the right rail.
  const [activeFileIdx, setActiveFileIdx] = useState(0);

  useEffect(() => {
    dispatch(DocumentActions.loadDocuments(noOp));
  }, [dispatch]);

  const doc = useMemo(() => documents.find((d) => String(d.id) === String(id)), [documents, id]);

  // Loading / not-found guards (after all hooks have run).
  if (!doc && documents.length === 0) {
    return (
      <>
        <Topbar crumbs={['Firm', 'Company Documents', '…']} />
        <div className="page">
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Loading document…
          </div>
        </div>
      </>
    );
  }
  if (!doc) {
    return (
      <>
        <Topbar crumbs={['Firm', 'Company Documents', 'Not found']} />
        <div className="page">
          <button className="back-link" type="button" onClick={() => navigate('/documents')}>
            <Icon name="back" size={14} />
            All documents
          </button>
          <div className="muted" style={{ padding: 40, textAlign: 'center' }}>
            Document <strong>#{id}</strong> not found.
          </div>
        </div>
      </>
    );
  }

  // Derived display ---------------------------------------------------------
  const files: FileData[] = Array.isArray(doc.data) ? doc.data : [];
  const activeFile = files[activeFileIdx];
  const activeFilename = activeFile?.name || `${doc.name}.bin`;
  const activeUrl = activeFile?.url || activeFile?.data;
  const activeExt = fileExtension(activeFilename) || 'BIN';
  const totalBytes = files.reduce((sum, f) => sum + (f.byte_size || 0), 0);
  const access = deriveAccess(doc);
  const typeLabel = DOC_TYPE_LABEL[doc.document_type] || doc.document_type || '—';

  return (
    <>
      <Topbar
        crumbs={['Firm', 'Company Documents', doc.name]}
        tools={
          <>
            <button className="btn ghost" type="button" onClick={() => navigate('/documents')}>
              <Icon name="back" size={13} />
              All documents
            </button>
            <button className="btn primary" type="button" onClick={() => setEditing(true)}>
              <Icon name="check" size={13} />
              Edit
            </button>
          </>
        }
      />
      {editing && <DocumentEditModal document={doc} onClose={() => setEditing(false)} />}
      <div className="page">
        {/* --- Hero card -------------------------------------------------- */}
        <div className="detail-hero">
          <div className="detail-hero-top">
            <div style={{ flexShrink: 0 }}>
              <FileIcon filename={activeFilename} />
            </div>
            <div className="meta">
              <div className="chips">
                <span className="chip muted mono">{activeExt}</span>
                <span className="chip gold">{typeLabel}</span>
                {access && <span className="chip muted">{access}</span>}
              </div>
              <h1>{doc.name}</h1>
              <div className="sub">
                {[doc.category, doc.user && `Uploaded by ${doc.user}`, doc.created_at]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
            <div className="value">
              <div className="eyebrow">Size</div>
              <div className="v">{formatBytes(totalBytes) || '—'}</div>
              <div className="sub">
                {files.length} file{files.length === 1 ? '' : 's'}
              </div>
            </div>
          </div>

          <div className="detail-hero-stats">
            <div className="stat">
              <div className="label">Type</div>
              <div className="value" style={{ fontSize: 16 }}>
                {typeLabel}
              </div>
            </div>
            <div className="stat">
              <div className="label">Category</div>
              <div className="value" style={{ fontSize: 16 }}>
                {doc.category || '—'}
              </div>
            </div>
            <div className="stat">
              <div className="label">Access scope</div>
              <div className="value" style={{ fontSize: 16 }}>
                {access ?? 'Not set'}
              </div>
            </div>
            <div className="stat">
              <div className="label">Files</div>
              <div className="value">{files.length}</div>
            </div>
          </div>
        </div>

        {/* No tab strip — Documents have a single Overview surface.  Tabs
            (Versions / Activity) can be added later once paper_trail is
            exposed per-document. */}

        <div className="grid detail">
          {/* LEFT column — preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            <div className="card flush">
              <div className="card-h">
                <h3>Preview</h3>
                {activeUrl && (
                  <div className="actions">
                    <a
                      className="btn ghost"
                      href={activeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Icon name="upload" size={13} />
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>
              {!activeUrl ? (
                <div className="muted" style={{ padding: 48, textAlign: 'center', fontSize: 13.5 }}>
                  No file URL available for this document.
                </div>
              ) : looksLikePdf(activeFilename) ? (
                // PDF: browsers render this natively via iframe.  Border + min
                // height matches the design's "preview slab" affordance.
                <iframe
                  title={activeFilename}
                  src={activeUrl}
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 360px)',
                    minHeight: 480,
                    border: 'none',
                    background: 'var(--surface-2)'
                  }}
                />
              ) : (
                <div style={{ padding: 60, textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', marginBottom: 14 }}>
                    <FileIcon filename={activeFilename} />
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: 18,
                      color: 'var(--ink)',
                      marginBottom: 4
                    }}
                  >
                    {activeFilename}
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 18 }}>
                    Browsers can't preview {activeExt} files inline — download or open in the
                    originating application.
                  </div>
                  <a
                    className="btn primary"
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon name="upload" size={13} />
                    Download {activeExt}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT column — metadata */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}>
            <div className="card">
              <h3
                style={{
                  margin: '0 0 14px 0',
                  fontFamily: 'var(--font-display)',
                  fontSize: 18,
                  fontWeight: 500,
                  color: 'var(--ink)'
                }}
              >
                Key details
              </h3>
              <KvRow label="Type" value={typeLabel} />
              <KvRow label="Category" value={doc.category || '—'} />
              <KvRow label="Sub-category" value={doc.sub_category || '—'} />
              <KvRow label="Access scope" value={access} />
              <KvRow label="Uploader" value={doc.user || '—'} />
              <KvRow label="Uploaded" value={doc.created_at || '—'} mono />
              <KvRow label="Total size" value={formatBytes(totalBytes) || '—'} mono />
            </div>

            <div className="card flush">
              <div className="card-h">
                <h3>Attached files</h3>
              </div>
              {files.length === 0 ? (
                <div className="muted" style={{ padding: 30, textAlign: 'center', fontSize: 13 }}>
                  No files attached.
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Filename</th>
                      <th className="right">Size</th>
                      <th>Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((f, i) => {
                      const fName = f.name || `file-${i + 1}`;
                      const isActive = i === activeFileIdx;
                      return (
                        <tr
                          key={f.id || `${fName}-${i}`}
                          onClick={() => setActiveFileIdx(i)}
                          style={{
                            cursor: 'pointer',
                            background: isActive ? 'var(--surface-2)' : undefined
                          }}
                          title={isActive ? 'Currently previewed' : 'Preview this file'}
                        >
                          <td className="strong">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span
                                aria-hidden
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: isActive ? 'var(--gold)' : 'transparent'
                                }}
                              />
                              {fName}
                            </div>
                          </td>
                          <td className="num right mono">{formatBytes(f.byte_size)}</td>
                          <td className="mono">{f.date || formatDate(f.created_at) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DocumentDetailV3;
